// CarPaymentDetails.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CarMessage.css";
import { useCarPaymentDetails } from "../payment-details/hooks/useCarPaymentDetails";
import { useOrderConfirmation } from "../payment-details/hooks/useOrderConfirmation";
import CustomerInfo from "../payment-details/components/CustomerInfo";
import ServiceList from "../payment-details/components/ServiceList";
import OrderLayout from "../payment-details/components/OrderLayout";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CarPrinterPopup from "../printer/components/CarPrinterPopup";
import { ToastContainer } from "react-toastify";

const CarPaymentDetails: React.FC = () => {
  const navigate = useNavigate();
  const { orderData, categories, error, isLoading, time, id, confirmOrder, updateCustomer, refetch } = useCarPaymentDetails();

  const { receivedAmount, setReceivedAmount, calculateRemainingAmount, handleConfirmOrder } = useOrderConfirmation({
    orderData,
    id,
    time,
    confirmOrder,
    updateCustomer,
  });

  const [showPopup, setShowPopup] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<any>(null);

  // Clear the payment flag when component mounts
  useEffect(() => {
    sessionStorage.removeItem("carWashFromPayment");
  }, []);

  const onConfirmClick = async () => {
    const confirmed = await handleConfirmOrder();
    if (confirmed) {
      setConfirmedOrder(confirmed);
      setShowPopup(true);
    }
  };

  const handleCancel = () => {
    // Set flag to preserve data when going back
    sessionStorage.setItem("carWashFromPayment", "true");
    navigate(-1);
  };

  if (isLoading) {
    return (
      <OrderLayout title="Order Details" onBack={() => navigate(-1)}>
        <div className="flex justify-center items-center ">
          <div className="text-center">
            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-blue-600 mx-auto "></div>
            <p className="text-gray-600 text-base font-medium">Loading order details...</p>
            <p className="text-gray-500 text-sm mt-1">Please wait while we fetch your information</p>
          </div>
        </div>
      </OrderLayout>
    );
  }

  if (error) {
    return (
      <OrderLayout title="Order Details" onBack={() => navigate(-1)}>
        <div className="flex flex-col items-center justify-center min-h-60 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Unable to Load Order</h3>
          <p className="text-gray-600 mb-4 max-w-md">{error}</p>
          <button onClick={refetch} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm">
            Try Again
          </button>
        </div>
      </OrderLayout>
    );
  }

  return (
    <OrderLayout title="Please verify your payment information and product details" onBack={() => navigate(-1)}>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 max-h-[calc(100vh-180px)] overflow-auto">
            {/* Left Column - Customer Info */}
            <div className="min-w-0">
              <CustomerInfo
                orderData={orderData}
                onReceivedAmountChange={setReceivedAmount}
                receivedAmount={receivedAmount}
                remainingAmount={calculateRemainingAmount()}
                onCancel={handleCancel}
                onConfirm={onConfirmClick}
              />
            </div>

            {/* Right Column - Service List */}
            <div className="min-w-0">
              <ServiceList orderData={orderData} categories={categories} />
            </div>
          </div>
        </div>
      </div>

      {showPopup && confirmedOrder && (
        <ModalWrapper title="Order Confirmed" onClose={() => setShowPopup(false)} width="max-w-xl" showCloseButton={true}>
          <CarPrinterPopup orderData={confirmedOrder} onClose={() => setShowPopup(false)} />
        </ModalWrapper>
      )}
      <ToastContainer />
    </OrderLayout>
  );
};

export default CarPaymentDetails;
